<script setup lang="ts">
/**
 * Cuánto le queda al plan, arriba a la derecha.
 *
 * Se enseña la ventana más apretada (la que primero te va a frenar) y, al
 * abrirla, el detalle de todas más lo que Chuy lleva gastado por su cuenta. Si el
 * proveedor no reporta límites — una llave de API, por ejemplo — se cae al gasto
 * de la casa, que siempre se puede contar.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import AppIcon from "./AppIcon.vue";
import UsoDetalle from "./UsoDetalle.vue";
import { useUsage } from "@/composables/useUsage";

const { usage } = useUsage();
const open = ref(false);
const route = useRoute();

// En el teléfono el detalle sube como hoja desde abajo, igual que el menú de la
// cuenta; en computadora cuelga del botón.
function alTeclear(evento: KeyboardEvent): void {
  if (evento.key === "Escape") open.value = false;
}
watch(() => route.fullPath, () => (open.value = false));
onMounted(() => document.addEventListener("keydown", alTeclear));
onBeforeUnmount(() => document.removeEventListener("keydown", alTeclear));

/** La ventana que va más llena: es la que de veras te limita. */
const tightest = computed(() => {
  const windows = usage.value?.windows ?? [];
  return windows.reduce<(typeof windows)[number] | null>(
    (worst, window) =>
      !worst || (window.utilization ?? 0) > (worst.utilization ?? 0) ? window : worst,
    null,
  );
});

const percent = computed(() => Math.round(tightest.value?.utilization ?? 0));

const tone = computed(() =>
  percent.value >= 85 ? "bad" : percent.value >= 60 ? "warn" : "ok",
);

const BAR: Record<string, string> = { ok: "bg-ok", warn: "bg-warn", bad: "bg-bad" };
const TEXT: Record<string, string> = { ok: "text-ok", warn: "text-warn", bad: "text-bad" };

function shortLabel(label: string): string {
  return label.replace("Ventana de ", "").replace("5 horas", "5 h");
}

/**
 * Qué se pinta cuando el plan no reporta topes.
 *
 * Antes, en ese caso el botón decía «Consumo» a secas — la palabra sola, sin un
 * número — y se leía como que no había nada que ver. Sí lo había: la
 * contabilidad de la casa siempre se puede calcular, porque sale de lo que cada
 * corrida dejó anotado. El plan es lo único que a veces no se sabe; lo gastado,
 * nunca.
 */
const gastoLocal = computed(() => {
  const local = usage.value?.local;
  if (!local) return "Consumo";
  if (local.costUsd > 0) return `$${local.costUsd.toFixed(2)}`;
  const tokens = local.inputTokens + local.outputTokens;
  if (tokens > 0) return `${Math.round(tokens / 1000).toLocaleString("es-MX")} k tokens`;
  return "Sin gasto";
});
</script>

<template>
  <div class="relative">
    <!--
      En el teléfono va compacto —sin la etiqueta de la ventana ni «7 días»—
      para caber junto al logotipo y el menú en 320px.
    -->
    <button
      class="toque flex items-center gap-1.5 rounded-[10px] border border-separator bg-surface px-2 py-1.5 text-[12px] transition-colors hover:bg-surface-hover md:gap-2 md:px-2.5"
      :title="usage?.detail ?? 'Consumo del modelo'"
      aria-label="Consumo del modelo"
      :aria-expanded="open"
      @click="open = !open"
    >
      <template v-if="usage?.available && tightest">
        <span class="hidden text-tertiary md:inline">{{ shortLabel(tightest.label) }}</span>
        <span class="h-1.5 w-10 overflow-hidden rounded-full bg-neutral-soft md:w-16">
          <span
            class="block h-full rounded-full transition-[width] duration-500 ease-[var(--ease-apple)]"
            :class="BAR[tone]"
            :style="{ width: `${Math.min(100, Math.max(2, percent))}%` }"
          />
        </span>
        <span class="font-semibold tabular-nums" :class="TEXT[tone]">{{ percent }}%</span>
      </template>
      <template v-else>
        <AppIcon name="sparkle" :size="13" class="text-tertiary" />
        <span class="hidden text-tertiary md:inline">7 días</span>
        <span class="font-semibold whitespace-nowrap tabular-nums text-secondary">{{ gastoLocal }}</span>
      </template>
      <AppIcon name="chevronRight" :size="12" class="hidden text-tertiary md:block" :class="open ? 'rotate-90' : ''" />
    </button>

    <!-- Computadora: el globito que cuelga del botón. -->
    <Transition name="modal">
      <div
        v-if="open"
        class="absolute top-full right-0 z-40 mt-2 hidden w-[290px] rounded-[14px] border border-separator bg-canvas-elevated p-3.5 shadow-float md:block"
      >
        <UsoDetalle />
      </div>
    </Transition>
    <!-- Un clic afuera cierra el panel, sin robarse el foco de la app. -->
    <div v-if="open" class="fixed inset-0 z-30 hidden md:block" @click="open = false" />
  </div>

  <!--
    Teléfono: la hoja, sacada al `body` por la misma razón que la del menú de la
    cuenta — la barra de arriba tiene desenfoque, y eso encierra a lo `fixed`.
  -->
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="open" class="md:hidden">
        <div class="fixed inset-0 z-40 bg-black/25 backdrop-blur-[2px]" @click="open = false" />
        <div
          class="modal-panel pb-safe-plus card fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] overflow-y-auto rounded-b-none px-4 pt-1.5"
          role="dialog"
          aria-label="Consumo del modelo"
        >
          <div class="flex justify-center pt-1.5 pb-2"><span class="h-1 w-9 rounded-full bg-separator-strong" /></div>
          <UsoDetalle />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
